import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { msalInstance, loginRequest } from '../config/msalConfig';
import { employeeService } from '../services/employeeService';

export type RoleType = 'respondent' | 'creator' | 'admin';

export interface User {
  id: string;
  email: string;
  name: string;
  fullName: string;
  role: RoleType;
  roleName: string;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  fetchCurrentUser: () => Promise<void>;
  setUser: (user: User) => void;
  viewAsRole: RoleType | null;
  setViewAsRole: (role: RoleType | null) => void;
  effectiveRole: RoleType | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<RoleType | null>(() => {
    const stored = localStorage.getItem('viewAsRole');
    return stored ? (stored as RoleType) : null;
  });

  const isAdmin = currentUser?.role === 'admin';
  const effectiveRole = isAdmin && viewAsRole ? viewAsRole : currentUser?.role ?? null;

  const setViewAsRole = useCallback((role: RoleType | null) => {
    if (currentUser?.role === 'admin') {
      setViewAsRoleState(role);
      if (role) {
        localStorage.setItem('viewAsRole', role);
      } else {
        localStorage.removeItem('viewAsRole');
      }
    }
  }, [currentUser?.role]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          const account = accounts[0];
          const email = account.username;

          if (email) {
            const employeeId = await employeeService.getEmployeeIdByEmail(email);
            if (employeeId) {
              const employeeData = await employeeService.getEmployeeById(employeeId);
              const role = employeeData.roleName ? getRoleFromRoleName(employeeData.roleName) : 'respondent';

              const user: User = {
                id: String(employeeData.id),
                email: employeeData.email,
                name: employeeData.fullName.split(' ')[0],
                fullName: employeeData.fullName,
                role,
                roleName: employeeData.roleName || 'FeedbackTaker',
              };

              setCurrentUser(user);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);


  const getRoleFromRoleName = (roleName: string): 'respondent' | 'creator' | 'admin' => {
    const roleMap: Record<string, 'respondent' | 'creator' | 'admin'> = {
      'FeedbackTaker': 'respondent',
      'FeedbackCreator': 'creator',
      'Admin': 'admin',
      'Administrator': 'admin',
    };
    return roleMap[roleName] || 'respondent';
  };

  const fetchCurrentUser = useCallback(async () => {
    try {
      setLoading(true);

      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const account = accounts[0];
        const email = account.username;

        if (email) {
          const employeeId = await employeeService.getEmployeeIdByEmail(email);
          if (employeeId) {
            const employeeData = await employeeService.getEmployeeById(employeeId);
            const role = employeeData.roleName ? getRoleFromRoleName(employeeData.roleName) : 'respondent';

            const user: User = {
              id: String(employeeData.id),
              email: employeeData.email,
              name: employeeData.fullName.split(' ')[0],
              fullName: employeeData.fullName,
              role,
              roleName: employeeData.roleName || 'FeedbackTaker',
            };

            setCurrentUser(user);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (_email: string, _password: string) => {
    setLoading(true);
    try {
      await msalInstance.initialize();
      const loginResponse = await msalInstance.loginPopup(loginRequest);

      if (loginResponse && loginResponse.account) {
        const email = loginResponse.account.username;

        if (email) {
          const employeeId = await employeeService.getEmployeeIdByEmail(email);
          if (employeeId) {
            const employeeData = await employeeService.getEmployeeById(employeeId);
            const role = employeeData.roleName ? getRoleFromRoleName(employeeData.roleName) : 'respondent';

            const user: User = {
              id: String(employeeData.id),
              email: employeeData.email,
              name: employeeData.fullName.split(' ')[0],
              fullName: employeeData.fullName,
              role,
              roleName: employeeData.roleName || 'FeedbackTaker',
            };

            setCurrentUser(user);
          } else {
            throw new Error(`Employee with email ${email} not found in system`);
          }
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await msalInstance.initialize();
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.logoutPopup({
          account: accounts[0],
        });
      }
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setCurrentUser(null);
    }
  }, []);

  const setUser = useCallback((user: User) => {
    setCurrentUser(user);
  }, []);

  const value = useMemo(() => ({
    currentUser,
    loading,
    login,
    logout,
    isAuthenticated: !!currentUser,
    fetchCurrentUser,
    setUser,
    viewAsRole,
    setViewAsRole,
    effectiveRole,
    isAdmin,
  }), [currentUser, loading, login, logout, fetchCurrentUser, setUser, viewAsRole, setViewAsRole, effectiveRole, isAdmin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
